import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { User, Mail, Lock, Save, Eye, EyeOff, CheckCircle, XCircle, Sparkles, Key, Copy, Trash2, Plus, Loader2 } from 'lucide-react'
import { restartOnboardingTour } from '@/components/OnboardingTour'
export default function Profile() {
  const { user, refreshUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  
  // Form states
  const [profileData, setProfileData] = useState({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
  })
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  // API Key states
  const [apiKeys, setApiKeys] = useState<string[]>([])
  const [apiKeyMapping, setApiKeyMapping] = useState<Record<string, string>>({}) // masked -> full key
  const [loadingKeys, setLoadingKeys] = useState(false)
  const [generatingKey, setGeneratingKey] = useState(false)
  const [newApiKey, setNewApiKey] = useState<string | null>(null)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      setProfileData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
      })
    }
  }, [user])

  useEffect(() => {
    loadApiKeys()
  }, [])

  const loadApiKeys = async () => {
    setLoadingKeys(true)
    try {
      const response = await apiClient.listApiKeys() as { success: boolean; api_keys?: string[]; _key_mapping?: Record<string, string> }
      if (response.success) {
        setApiKeys(response.api_keys || [])
        // Store the key mapping if available (for revocation)
        if (response._key_mapping) {
          setApiKeyMapping(response._key_mapping)
        }
      }
    } catch (error: any) {
      console.error('Failed to load API keys:', error)
    } finally {
      setLoadingKeys(false)
    }
  }

  const handleGenerateApiKey = async () => {
    setGeneratingKey(true)
    try {
      const response = await apiClient.generateApiKey() as { success: boolean; api_key?: string }
      if (response.success && response.api_key) {
        setNewApiKey(response.api_key)
        await loadApiKeys()
        alert('API key generated! Make sure to copy it - it won\'t be shown again.')
      }
    } catch (error: any) {
      alert(`Failed to generate API key: ${error.message || 'Unknown error'}`)
    } finally {
      setGeneratingKey(false)
    }
  }

  const handleRevokeApiKey = async (maskedKey: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return
    }

    setLoadingKeys(true)
    try {
      // Get the full key from mapping or use the new key if it matches
      let fullKey: string | null = null
      
      if (newApiKey && maskedKey.startsWith(newApiKey.substring(0, 11))) {
        // This is the newly generated key
        fullKey = newApiKey
      } else if (apiKeyMapping[maskedKey]) {
        // Get from mapping
        fullKey = apiKeyMapping[maskedKey]
      }
      
      if (!fullKey) {
        alert('Unable to find full API key. Please try refreshing the page.')
        return
      }

      await apiClient.revokeApiKey(fullKey)
      await loadApiKeys()
      if (newApiKey === fullKey) {
        setNewApiKey(null) // Clear new key if it was revoked
      }
      alert('API key revoked successfully')
    } catch (error: any) {
      alert(`Failed to revoke API key: ${error.message || 'Unknown error'}`)
    } finally {
      setLoadingKeys(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(text)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch (error) {
      alert('Failed to copy to clipboard')
    }
  }

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      await apiClient.updateUserProfile({
        email: profileData.email,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
      })
      
      await refreshUser()
      alert('Profile updated successfully!')
    } catch (error: any) {
      alert(`Failed to update profile: ${error.message || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('New password and confirmation do not match.')
      return
    }
    
    if (passwordData.new_password.length < 8) {
      alert('Password must be at least 8 characters long.')
      return
    }
    
    setLoading(true)
    
    try {
      await apiClient.updatePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      })
      
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      })
      
      alert('Password updated successfully!')
    } catch (error: any) {
      alert(`Failed to update password: ${error.message || 'Please check your current password'}`)
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-300">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Profile Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Manage your account information and security settings
        </p>
      </div>

      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="w-5 h-5 mr-2" />
            Account Information
          </CardTitle>
          <CardDescription>
            Update your personal information and email address
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                required
              />
            </div>

            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center space-x-4">
                <Badge variant="outline">
                  Role: {user.role || 'user'}
                </Badge>
                <Badge variant="outline">
                  Tier: {user.subscription_tier || 'starter'}
                </Badge>
                <Badge variant="outline">
                  Requests: {user.requests_used || 0} / {user.request_limit === -1 ? '∞' : user.request_limit || 0}
                </Badge>
              </div>
              <Button type="submit" disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Lock className="w-5 h-5 mr-2" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div>
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters long
              </p>
            </div>
            
            <div>
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
              />
              {passwordData.new_password && passwordData.confirm_password && (
                <div className="mt-1 flex items-center space-x-1">
                  {passwordData.new_password === passwordData.confirm_password ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-green-500">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-xs text-red-500">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="pt-4">
              <Button type="submit" disabled={loading}>
                <Lock className="w-4 h-4 mr-2" />
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* API Keys Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="w-5 h-5 mr-2" />
            API Keys
          </CardTitle>
          <CardDescription>
            Generate and manage API keys for programmatic access to all endpoints
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* New API Key Display */}
          {newApiKey && (
            <div className="p-4 bg-green-500/10 border border-green-500/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-green-400 font-semibold">New API Key Generated</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setNewApiKey(null)
                  }}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={newApiKey}
                  readOnly
                  className="font-mono text-sm bg-black/50"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(newApiKey)}
                >
                  {copiedKey === newApiKey ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-yellow-400 mt-2">
                ⚠️ Store this key securely - it won't be shown again!
              </p>
            </div>
          )}

          {/* Generate New Key Button */}
          <Button
            onClick={handleGenerateApiKey}
            disabled={generatingKey}
            className="w-full"
          >
            {generatingKey ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Generate New API Key
              </>
            )}
          </Button>

          {/* API Keys List */}
          <div className="space-y-2">
            <Label>Your API Keys ({apiKeys.length})</Label>
            {loadingKeys ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              </div>
            ) : apiKeys.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No API keys generated yet
              </p>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((maskedKey, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-900/50 border border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Key className="w-4 h-4 text-gray-400" />
                      <code className="text-sm font-mono text-gray-300">{maskedKey}</code>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Try to extract and copy the key (if it's the new one)
                          if (newApiKey && maskedKey.startsWith(newApiKey.split('...')[0])) {
                            copyToClipboard(newApiKey)
                          } else {
                            copyToClipboard(maskedKey)
                          }
                        }}
                      >
                        {copiedKey === maskedKey ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeApiKey(maskedKey)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-400">
              Use API keys in the <code className="text-gray-300">X-API-Key</code> header for authentication.
              API keys provide the same access as your user account.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Tour Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Sparkles className="w-5 h-5 mr-2" />
            Platform Tour
          </CardTitle>
          <CardDescription>
            Restart the interactive tour to learn about platform features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline" 
            onClick={restartOnboardingTour}
            className="w-full"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Restart Onboarding Tour
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

